import {
  UpdateAccountUIPreferencesApi,
  type Preferences,
  GetAccountUIPreferencesApi,
  GetViewerUIPreferencesApi,
  UpdateViewerUIPreferencesApi,
  type Configuration,
} from '@repo/api-gw-sdk';
import { mutate } from 'swr';

import type { HttpClient } from './httpClient';
import type { PreferencesKey, PreferencesMap } from './preferencesKey';

interface ClientPreferences<K extends PreferencesKey> extends Preferences {
  value?: PreferencesMap[K];
}

const baseUrl = '/preferences';

export const preferences = (httpClient: HttpClient, config: Configuration) => {
  const getAccountUIPreferencesApi = new GetAccountUIPreferencesApi(config);
  const updateAccountUIPreferencesApi = new UpdateAccountUIPreferencesApi(
    config
  );
  const getViewerUIPreferencesApi = new GetViewerUIPreferencesApi(config);
  const updateViewerUIPreferencesApi = new UpdateViewerUIPreferencesApi(config);

  return {
    getUserPref: <K extends keyof PreferencesMap>(key: K | undefined) =>
      httpClient.execute(
        [baseUrl, 'get-user-pref', key],
        async (): Promise<ClientPreferences<K> | undefined> => {
          if (!key) {
            return undefined;
          }

          return getViewerUIPreferencesApi
            .getViewerUIPreferences(key)
            .catch((x) => {
              if (x.code !== 404) {
                throw x;
              }
            }) as Promise<ClientPreferences<K>>;
        }
      ),
    getAccountPref: <K extends keyof PreferencesMap>(key: K) =>
      httpClient.execute(
        [baseUrl, 'get-account-pref', key],
        (): Promise<ClientPreferences<K>> =>
          getAccountUIPreferencesApi.getAccountUIPreferences(key).catch((x) => {
            if (x.code !== 404) {
              throw x;
            }
          }) as Promise<ClientPreferences<K>>
      ),
    updateAccountPref: <K extends PreferencesKey>(
      key: K,
      value: PreferencesMap[K]
    ) =>
      updateAccountUIPreferencesApi
        .updateAccountUIPreferences(key, value)
        .then(() => mutate([baseUrl, 'get-account-pref', key])),
    updateUserPref: <K extends PreferencesKey>(
      key: K,
      value: PreferencesMap[K]
    ) =>
      updateViewerUIPreferencesApi
        .updateViewerUIPreferences(key, value)
        .then(() => mutate([baseUrl, 'get-user-pref', key])),
  };
};
